import React, { useContext, lazy } from 'react';

import ProductSelector from '../ProductSelector';
import UnknownCustomerForm from '../UnknownCustomerForm';
import IdentifiedCustomerForm from '../IdentifiedCustomerForm';
import LazyComponent from '../base/LazyComponent';

// Lazy-loaded dialog content components
// These are only loaded when their respective dialogs are opened
const CancelAppointmentView = lazy(() => import('../CancelAppointmentView'));
const ConfirmationView = lazy(() => import('../ConfirmationView'));
const LoginView = lazy(() => import('../LoginView'));

import {
  ProductPageContext,
  CustomerFormContext,
  CancelPageContext,
  ConfirmationPageContext,
  LoginPageContext,
  ErrorContext,
} from '../Calendar';
import { useSelector } from 'react-redux';
import { selectAppConfigProp } from '../../slices/appState';

export default function DefaultInterfacesHandler({
  utilizeProductInterface = true,
  utilizeUnknownCustomerInterface = true,
  utilizeIdentifiedCustomerInterface = true,
  utilizeCancelAppointmentInterface = true,
  utilizeConfirmationInterface = true,
  utilizeLoginInterface = true,
  utilizeErrorDialogInterface = true,
}) {
  const ProductPage = useSelector((state) =>
    selectAppConfigProp(state, 'interfaces.ProductPage'),
  );
  const UnkownBookingPage = useSelector((state) =>
    selectAppConfigProp(state, 'interfaces.UnkownBookingPage'),
  );
  const IdentifiedBookingPage = useSelector((state) =>
    selectAppConfigProp(state, 'interfaces.IdentifiedBookingPage'),
  );
  const CancelPage = useSelector((state) =>
    selectAppConfigProp(state, 'interfaces.CancelPage'),
  );
  const ConfirmationPage = useSelector((state) =>
    selectAppConfigProp(state, 'interfaces.ConfirmationPage'),
  );
  const LoginPage = useSelector((state) =>
    selectAppConfigProp(state, 'interfaces.LoginPage'),
  );
  const ErrorDialog = useSelector((state) =>
    selectAppConfigProp(state, 'interfaces.ErrorDialog'),
  );

  const productContext = useContext(ProductPageContext);
  const bookingPageContext = useContext(CustomerFormContext);
  const cancelPageContext = useContext(CancelPageContext);
  const confirmationPageContext = useContext(ConfirmationPageContext);
  const loginPageContext = useContext(LoginPageContext);
  const errorContext = useContext(ErrorContext);

  return (
    <>
      {utilizeProductInterface && ProductPage && (
        <ProductPage {...productContext.pageContainerProps}>
          <ProductSelector
            onProductSelected={productContext?.pageContainerProps?.onClose}
          />
        </ProductPage>
      )}
      {utilizeIdentifiedCustomerInterface && IdentifiedBookingPage && (
        <IdentifiedBookingPage
          {...bookingPageContext.pageContainerProps}
          open={bookingPageContext?.pageContainerProps?.identifiedOpen}
        >
          <IdentifiedCustomerForm {...bookingPageContext} />
        </IdentifiedBookingPage>
      )}
      {utilizeUnknownCustomerInterface && UnkownBookingPage && (
        <UnkownBookingPage
          {...bookingPageContext.pageContainerProps}
          open={bookingPageContext?.pageContainerProps?.unknownOpen}
        >
          <UnknownCustomerForm {...bookingPageContext} />
        </UnkownBookingPage>
      )}
      {utilizeCancelAppointmentInterface && CancelPage && (
        <CancelPage {...cancelPageContext.pageContainerProps}>
          <LazyComponent>
            <CancelAppointmentView />
          </LazyComponent>
        </CancelPage>
      )}
      {utilizeConfirmationInterface && ConfirmationPage && (
        <ConfirmationPage {...confirmationPageContext.pageContainerProps}>
          <LazyComponent>
            <ConfirmationView />
          </LazyComponent>
        </ConfirmationPage>
      )}
      {utilizeLoginInterface && LoginPage && (
        <LoginPage {...loginPageContext.pageContainerProps}>
          <LazyComponent>
            <LoginView doOnSuccess={loginPageContext?.doOnSuccess} />
          </LazyComponent>
        </LoginPage>
      )}
      {utilizeErrorDialogInterface && ErrorDialog && (
        <ErrorDialog {...errorContext.pageContainerProps} />
      )}
    </>
  );
}
